// filepath: apps/web/src/features/admin/effects/EmailChannelsTable.tsx
//
// FR-UI-ADM-8 -- data table for the email channel admin editor.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { EmailChannel } from "./types";

interface EmailChannelsTableProps {
  channels: EmailChannel[];
  onDisable: (id: string, version: number) => void | Promise<void>;
  onDelete: (id: string, version: number) => void | Promise<void>;
  isPending: boolean;
}

export function EmailChannelsTable({
  channels,
  onDisable,
  onDelete,
  isPending,
}: EmailChannelsTableProps): React.ReactElement {
  const { t } = useTranslation();

  if (channels.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="email-channels-empty"
      >
        {t("admin.effects.email.empty")}
      </p>
    );
  }

  return (
    <table
      className="w-full border-collapse text-sm"
      data-testid="email-channels-table"
      aria-label={t("admin.effects.email.table_aria_label")}
    >
      <thead>
        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
          <th className="px-3 py-2">{t("admin.effects.email.column.from")}</th>
          <th className="px-3 py-2">
            {t("admin.effects.email.column.subject")}
          </th>
          <th className="px-3 py-2">
            {t("admin.effects.email.column.locales")}
          </th>
          <th className="px-3 py-2">
            {t("admin.effects.email.column.status")}
          </th>
          <th className="px-3 py-2">
            {t("admin.effects.email.column.actions")}
          </th>
        </tr>
      </thead>
      <tbody>
        {channels.map((ch) => {
          const locales = [
            ch.body.ru ? "ru" : null,
            ch.body.kk ? "kk" : null,
            ch.body.en ? "en" : null,
          ].filter(Boolean);
          return (
            <tr
              key={ch.id}
              className="border-b hover:bg-muted/40"
              data-testid={`email-channel-row-${ch.id}`}
            >
              <td className="px-3 py-2 text-xs">{ch.from_address}</td>
              <td className="px-3 py-2 text-xs">{ch.subject}</td>
              <td className="px-3 py-2">
                <div className="flex gap-1">
                  {locales.map((l) => (
                    <span
                      key={l}
                      className="rounded bg-muted px-1 py-0.5 text-xs uppercase"
                      data-locale={l}
                    >
                      {l}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-3 py-2">
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
                  data-status={ch.status}
                >
                  <span aria-hidden="true">
                    {ch.status === "active" ? "●" : "○"}
                  </span>
                  {t(`admin.effects.status.${ch.status}`)}
                </span>
              </td>
              <td className="px-3 py-2">
                <div className="flex gap-2">
                  {ch.status === "active" && (
                    <button
                      type="button"
                      className="min-h-[48px] min-w-[48px] rounded border border-border px-2 py-1 text-xs"
                      aria-label={t("admin.effects.email.action.disable")}
                      disabled={isPending}
                      onClick={() => void onDisable(ch.id, ch.row_version)}
                      data-testid={`email-channel-disable-${ch.id}`}
                    >
                      {t("admin.effects.email.action.disable")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="min-h-[48px] min-w-[48px] rounded border border-destructive px-2 py-1 text-xs text-destructive"
                    aria-label={t("admin.effects.email.action.delete")}
                    disabled={isPending}
                    onClick={() => void onDelete(ch.id, ch.row_version)}
                    data-testid={`email-channel-delete-${ch.id}`}
                  >
                    {t("admin.effects.email.action.delete")}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
